import constant from 'lodash/constant'
import { ROUTE_API_DOCUMENTS } from '@klear/constants'
import type { DocumentType } from '@klear/constants/document-types'
import { UPLOAD_INVALID_TYPE_MESSAGE, isUploadContentType } from '@klear/upload/upload-formats'
import { getApiUrl } from '@klear/runtime-env'
import type { DocumentDestination } from '@klear/schema/documents'
import type { UploadHints } from '@klear/schema/document-processing'
import { splitFileName } from '@klear/upload/file-name'

export type DocumentUploadDetails = {
  type?: Exclude<DocumentType, 'unclassified'>
  notes?: string
  fileOn: DocumentDestination
  batchSize?: number
}

export function unsupportedDocumentFiles(files: readonly File[]): string | null {
  const refused = files.filter((file) => !isUploadContentType(file.type))
  if (refused.length === 0) return null
  return `${refused.map((file) => file.name).join(', ')}: ${UPLOAD_INVALID_TYPE_MESSAGE}`
}

function postDocument(traderId: number, file: File, data: Record<string, unknown>): Promise<Response> {
  const body = new FormData()
  body.append('data', JSON.stringify({ name: splitFileName(file.name).base.trim() || file.name, ...data }))
  body.append('file', file)
  return fetch(`${getApiUrl()}/${ROUTE_API_DOCUMENTS}?traderId=${traderId}`, {
    method: 'POST',
    body,
    credentials: 'include'
  })
}

export async function sendDocumentForReview(
  traderId: number,
  file: File,
  review: { hints: UploadHints | null; uploadRequestToken: string | null }
): Promise<{ uploadRequestRecorded: boolean | null }> {
  const response = await postDocument(traderId, file, {
    review: review.hints === null ? {} : { hints: review.hints },
    ...(review.uploadRequestToken !== null && { uploadRequestToken: review.uploadRequestToken })
  })
  const result = (await response.json().catch(constant(null))) as {
    error?: string
    uploadRequestRecorded?: boolean | null
  } | null
  if (!response.ok) throw new Error(result?.error ?? 'Upload failed')
  return { uploadRequestRecorded: result?.uploadRequestRecorded ?? null }
}

export async function uploadDocument(
  traderId: number,
  file: File,
  details: DocumentUploadDetails
): Promise<{ documentId: number | null; name: string | null }> {
  const response = await postDocument(traderId, file, details)
  const result = (await response.json().catch(constant(null))) as {
    error?: string
    document?: { document?: { id?: number; name?: string } }
  } | null
  if (!response.ok) throw new Error(result?.error ?? 'Could not upload the document.')
  return { documentId: result?.document?.document?.id ?? null, name: result?.document?.document?.name ?? null }
}
